import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  limparSessaoAuth,
  lerSessaoAuth,
  persistirSessaoAuth,
  possuiSessaoSalva,
  removerTokenLegado,
} from '../services/authStorage';
import { definirCsrfToken, definirSessaoEsperada, notificarSessaoTrocada, sessaoDivergeDaAba } from '../services/api';
import * as authService from '../services/authService';
import { definirFusoAgenda } from '../services/fusoAgenda';
import { definirMaxGruposLote, definirPassoAgendaMin, definirSessoesMaximo, definirTussObrigatorio } from '../services/limitesAgenda';

export const AuthContext = createContext(null);

function montarUsuario(usuarioApi) {
  if (!usuarioApi) return null;
  return {
    id: usuarioApi.id ?? usuarioApi.usuarioId ?? null,
    login: usuarioApi.login ?? null,
    nome: usuarioApi.nome ?? null,
  };
}

function montarEstadoInicial() {
  // Versão antiga guardava o token no localStorage: some na primeira carga (onda OA)
  removerTokenLegado();
  const sessao = lerSessaoAuth();
  // P-S10: a sessão desta aba nasce da que estava salva; o /me confirma (ou corrige) logo em seguida
  definirSessaoEsperada({ usuarioId: sessao.usuario?.id, empresaId: sessao.empresaId, filialId: sessao.filialId });
  return {
    empresaId: sessao.empresaId,
    filialId: sessao.filialId,
    usuario: sessao.usuario,
    // Sessão salva: a tela espera o GET /me confirmar o cookie antes de abrir as rotas
    carregandoSessao: possuiSessaoSalva(),
    empresasPermitidas: [],
    // O0: perfil na empresa e profissional vinculado — vêm sempre do back (login/me), nunca do armazenamento
    perfil: null,
    profissional: null,
    // O0c: perfil da empresa (nome) e nível por rotina — também só do back
    perfilId: null,
    perfilNome: null,
    permissoes: null,
    // F2.1: Admin Clínico (lê todo o prontuário) — só do back
    acessoClinicoTotal: false,
    // O0b: senha provisória — só a tela "Definir sua senha" abre até a troca
    trocarSenha: false,
    // empresa de teste (990) em produção: o front mostra a faixa; quem decide é sempre o back
    empresaTeste: false,
  };
}

/** Campos de acesso que o back devolve (login, /me, selecionar-contexto). */
const CAMPOS_ACESSO = ['perfil', 'profissional', 'perfilId', 'perfilNome', 'permissoes', 'acessoClinicoTotal'];
const INTERVALO_MIN_ATUALIZAR_MS = 5000;

/**
 * AuthContext — fonte de verdade de empresaId/filialId/login (Modelo A).
 * Onda OA: o token fica num cookie httpOnly (o front não o vê); aqui só ficam os dados de exibição
 * e o token CSRF em memória (services/api.js). Docs: src/docs/plano-adequacao-telas.md.
 * @param {Function} [onLimparEstadoTenant] callback para resetar contexts de domínio
 */
export function AuthProvider({ children, onLimparEstadoTenant }) {
  const [estadoAuth, setEstadoAuth] = useState(montarEstadoInicial);

  const aplicarSessao = useCallback((payload) => {
    const usuario = montarUsuario(payload.usuario);
    const sessao = {
      empresaId: payload.empresaId != null ? String(payload.empresaId) : null,
      filialId: payload.filialId != null ? String(payload.filialId) : null,
      usuario,
    };
    if (payload.csrfToken !== undefined) definirCsrfToken(payload.csrfToken);
    // Fuso da agenda vem do back: dia/hora da agenda são lidos nele (não no fuso do navegador)
    if (payload.fusoAgenda !== undefined) definirFusoAgenda(payload.fusoAgenda);
    // LP2: profissionais por agendamento em lote (AGENDA_MAX_GRUPOS_LOTE no back)
    if (payload.maxGruposLote !== undefined) definirMaxGruposLote(payload.maxGruposLote);
    // Passo da agenda (MV_AGENDAMIN): minuto de início e horários sugeridos
    if (payload.passoAgendaMin !== undefined) definirPassoAgendaMin(payload.passoAgendaMin);
    // Controle do TUSS (MV_TUSSOBRIG): a tabela de preços só exige tabela e código TUSS quando ligado
    if (payload.tussObrigatorio !== undefined) definirTussObrigatorio(payload.tussObrigatorio);
    // Máximo de sessões por atendimento do pacote (MV_SESSMAXAT): limita o campo da tela de pacotes
    if (payload.sessoesMaximo !== undefined) definirSessoesMaximo(payload.sessoesMaximo);
    definirSessaoEsperada({ usuarioId: usuario?.id, empresaId: sessao.empresaId, filialId: sessao.filialId });
    persistirSessaoAuth(sessao);
    setEstadoAuth((anterior) => ({
      ...anterior,
      ...sessao,
      carregandoSessao: false,
      empresasPermitidas: payload.empresasPermitidas ?? anterior.empresasPermitidas,
      ...Object.fromEntries(
        CAMPOS_ACESSO.map((c) => [c, payload[c] !== undefined ? payload[c] || null : anterior[c]])
      ),
      trocarSenha: payload.trocarSenha !== undefined ? Boolean(payload.trocarSenha) : anterior.trocarSenha,
      empresaTeste: payload.empresaTeste !== undefined ? Boolean(payload.empresaTeste) : anterior.empresaTeste,
    }));
  }, []);

  const logout = useCallback(() => {
    // O back apaga o cookie; falha de rede não impede sair localmente
    authService.logout().catch(() => {});
    limparSessaoAuth();
    definirCsrfToken(null);
    definirSessaoEsperada(null);
    definirFusoAgenda(null);
    setEstadoAuth({
      empresaId: null,
      filialId: null,
      usuario: null,
      carregandoSessao: false,
      empresasPermitidas: [],
      perfil: null,
      profissional: null,
      perfilId: null,
      perfilNome: null,
      permissoes: null,
      acessoClinicoTotal: false,
      trocarSenha: false,
      empresaTeste: false,
    });
    if (typeof onLimparEstadoTenant === 'function') {
      onLimparEstadoTenant();
    }
  }, [onLimparEstadoTenant]);

  const campoDeAcesso = (dados) => Object.fromEntries(CAMPOS_ACESSO.map((c) => [c, dados[c]]));

  const login = useCallback(
    async ({ login: loginUsuario, senha, empresaId, filialId }) => {
      const resposta = await authService.login({
        login: loginUsuario,
        senha,
        empresaId,
        filialId,
      });
      const dados = resposta?.data ?? resposta;
      aplicarSessao({
        csrfToken: dados.csrfToken,
        empresaId: dados.empresaId,
        filialId: dados.filialId,
        usuario: dados.usuario,
        ...campoDeAcesso(dados),
        trocarSenha: dados.trocarSenha,
        empresaTeste: dados.empresaTeste,
        fusoAgenda: dados.fusoAgenda,
        maxGruposLote: dados.maxGruposLote,
        passoAgendaMin: dados.passoAgendaMin,
        tussObrigatorio: dados.tussObrigatorio,
        sessoesMaximo: dados.sessoesMaximo,
      });
      return dados;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aplicarSessao]
  );

  /** Troca da própria senha: a sessão continua (cookie novo) e a troca obrigatória acaba. */
  const trocarSenha = useCallback(
    async ({ senhaAtual, novaSenha }) => {
      const resposta = await authService.trocarSenha({ senhaAtual, novaSenha });
      const dados = resposta?.data ?? resposta;
      definirCsrfToken(dados.csrfToken);
      setEstadoAuth((anterior) => ({ ...anterior, trocarSenha: false }));
      return dados;
    },
    []
  );

  const selecionarContexto = useCallback(
    async ({ empresaId, filialId }) => {
      const resposta = await authService.selecionarContexto({ empresaId, filialId });
      const dados = resposta?.data ?? resposta;
      aplicarSessao({
        csrfToken: dados.csrfToken,
        empresaId: dados.empresaId,
        filialId: dados.filialId,
        usuario: estadoAuth.usuario,
        ...campoDeAcesso(dados),
        empresaTeste: dados.empresaTeste,
        fusoAgenda: dados.fusoAgenda,
        maxGruposLote: dados.maxGruposLote,
        passoAgendaMin: dados.passoAgendaMin,
        tussObrigatorio: dados.tussObrigatorio,
        sessoesMaximo: dados.sessoesMaximo,
      });
      if (typeof onLimparEstadoTenant === 'function') {
        onLimparEstadoTenant();
      }
      return dados;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aplicarSessao, estadoAuth.usuario, onLimparEstadoTenant]
  );

  /** Confirma a sessão do cookie e recupera o CSRF (perdido ao recarregar a página). */
  const carregarMe = useCallback(async () => {
    if (!possuiSessaoSalva()) {
      setEstadoAuth((anterior) => ({ ...anterior, carregandoSessao: false }));
      return null;
    }
    try {
      setEstadoAuth((anterior) => ({ ...anterior, carregandoSessao: true }));
      const resposta = await authService.me();
      const dados = resposta?.data ?? resposta;
      aplicarSessao({
        csrfToken: dados.csrfToken ?? null,
        empresaId: dados.empresaId,
        filialId: dados.filialId,
        usuario: dados.usuario,
        empresasPermitidas: dados.empresasPermitidas,
        ...campoDeAcesso(dados),
        trocarSenha: dados.trocarSenha,
        empresaTeste: dados.empresaTeste,
        fusoAgenda: dados.fusoAgenda,
        maxGruposLote: dados.maxGruposLote,
        passoAgendaMin: dados.passoAgendaMin,
        tussObrigatorio: dados.tussObrigatorio,
        sessoesMaximo: dados.sessoesMaximo,
      });
      return dados;
    } catch (erro) {
      logout();
      throw erro;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aplicarSessao, logout]);

  /**
   * O0c: o administrador mudou as permissões com a pessoa logada (a API respondeu 403 SEM_PERMISSAO).
   * Relê o /me em segundo plano — sem tela de carregamento e sem deslogar; no máximo uma vez a cada 5 s.
   */
  const ultimaAtualizacao = useRef(0);
  const atualizarAcesso = useCallback(async ({ forcar = false } = {}) => {
    const agora = Date.now();
    if (!forcar && agora - ultimaAtualizacao.current < INTERVALO_MIN_ATUALIZAR_MS) return null;
    ultimaAtualizacao.current = agora;
    try {
      const resposta = await authService.me();
      const dados = resposta?.data ?? resposta;
      // P-S10: o cookie já é de outra sessão (outra aba entrou): não adota o CSRF nem as permissões dela — bloqueia a aba
      if (sessaoDivergeDaAba({ usuario: dados.usuario, empresaId: dados.empresaId, filialId: dados.filialId })) {
        notificarSessaoTrocada({ sessaoNova: { usuario: montarUsuario(dados.usuario), empresaId: dados.empresaId, filialId: dados.filialId } });
        return null;
      }
      // CSRF perdido e senha provisória nova também chegam como 403: o /me traz os dois de volta
      if (dados.csrfToken !== undefined) definirCsrfToken(dados.csrfToken);
      setEstadoAuth((anterior) => ({
        ...anterior,
        ...campoDeAcesso(dados),
        trocarSenha: dados.trocarSenha !== undefined ? Boolean(dados.trocarSenha) : anterior.trocarSenha,
      }));
      return dados;
    } catch (e) {
      return null; // 401 já é tratado pelo cliente da API (volta ao login)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const estaAutenticado = Boolean(estadoAuth.usuario);

  // O0c: o que services/permissoes.js usa para rotas, menu e botões
  const acesso = useMemo(
    () => ({ perfil: estadoAuth.perfil, permissoes: estadoAuth.permissoes, acessoClinicoTotal: Boolean(estadoAuth.acessoClinicoTotal) }),
    [estadoAuth.perfil, estadoAuth.permissoes, estadoAuth.acessoClinicoTotal]
  );

  const valor = useMemo(
    () => ({
      empresaId: estadoAuth.empresaId,
      filialId: estadoAuth.filialId,
      usuario: estadoAuth.usuario,
      perfil: estadoAuth.perfil,
      profissional: estadoAuth.profissional,
      perfilId: estadoAuth.perfilId,
      perfilNome: estadoAuth.perfilNome,
      permissoes: estadoAuth.permissoes,
      acessoClinicoTotal: Boolean(estadoAuth.acessoClinicoTotal),
      empresaTeste: Boolean(estadoAuth.empresaTeste),
      acesso,
      atualizarAcesso,
      trocarSenha: estadoAuth.trocarSenha,
      definirSenha: trocarSenha,
      empresasPermitidas: estadoAuth.empresasPermitidas,
      carregandoSessao: estadoAuth.carregandoSessao,
      estaAutenticado,
      login,
      logout,
      selecionarContexto,
      carregarMe,
      aplicarSessao,
    }),
    [
      estadoAuth,
      acesso,
      atualizarAcesso,
      estaAutenticado,
      trocarSenha,
      login,
      logout,
      selecionarContexto,
      carregarMe,
      aplicarSessao,
    ]
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const contexto = useContext(AuthContext);
  if (!contexto) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return contexto;
}
